import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import sanitizeHtml from 'sanitize-html';
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { InfoRounded, LockRounded } from '@mui/icons-material';
import NotificationDialogV2 from '@src/components/NotificationDialogV2';
import { FREE_PACKAGE_CODES } from '@src/constants/package';
import { checkCustomPackage } from '@src/services/package';
import theme from '@src/styles/theme';
import { StyledRemainingCredits } from './index.style';

const RemainingCredits = ({ credits, isLocked }) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const { usingPackage } = useSelector((state) => state.user);
  const { user: userInfo } = useSelector((state) => state.auth);
  const [showPopupBonusCredits, setShowPopupBonusCredits] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('smd'));
  const [showInfoCustomCredits, setShowInfoCustomCredits] = useState(false);

  const isUsingCustomPackage = checkCustomPackage({
    type: usingPackage?.type,
    level: usingPackage?.level,
  });

  const {
    introductoryDailyCredits,
    introductoryPeriodDays,
    regularDailyCredits,
  } = userInfo?.studioUsageOptions?.freemiumConfig || {};
  const isFreePackage = FREE_PACKAGE_CODES.includes(usingPackage?.code);

  const renderButtonGotIt = (handleClick) => (
    <div className="used-credits-button-container">
      <Button
        className="button-got-it"
        variant="contained"
        onClick={handleClick}
      >
        {t('gotItV2')}
      </Button>
    </div>
  );

  const handleClickShowBonusCredits = () => {
    if (isMobile) setShowPopupBonusCredits(true);
    return null;
  };

  const handleClickShowInfoCustomPackage = () => {
    if (isMobile) setShowInfoCustomCredits(true);
    return null;
  };

  const renderLockIcon = () => {
    if (isUsingCustomPackage)
      return (
        <Tooltip
          arrow
          className="tootlip"
          title={t('lockCycleCreditsTooltipMyPackage')}
          placement="left"
        >
          <IconButton onClick={handleClickShowInfoCustomPackage}>
            <LockRounded sx={{ fontSize: 16 }} className="lock-icon" />
          </IconButton>
        </Tooltip>
      );
    return <LockRounded sx={{ fontSize: 16 }} className="lock-icon" />;
  };

  return (
    <StyledRemainingCredits>
      <Box className="remaining-credits-container">
        <Typography className="label">{t('remainingCharacters')}</Typography>
        <Box className="remaining-content-container">
          {!!isLocked && renderLockIcon()}
          <div className="remaining-credits-container">
            <Typography className="content">{credits}</Typography>
            {isFreePackage && (
              <Tooltip
                arrow
                className="tooltip"
                title={
                  <Typography
                    component="span"
                    sx={{ fontSize: '12px' }}
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(
                        t('introductoryPeriodDays', {
                          introductoryPeriodDays,
                          introductoryDailyCredits:
                            introductoryDailyCredits?.toLocaleString(language),
                          regularDailyCredits:
                            regularDailyCredits?.toLocaleString(language),
                        }),
                      ),
                    }}
                  />
                }
                placement="left"
              >
                <IconButton onClick={handleClickShowBonusCredits}>
                  <InfoRounded color="gray" className="icon" fontSize="16px" />
                </IconButton>
              </Tooltip>
            )}
          </div>
        </Box>
        <NotificationDialogV2
          showIcon={false}
          title={t('detailInfo')}
          description={t('introductoryPeriodDays', {
            introductoryPeriodDays,
            introductoryDailyCredits:
              introductoryDailyCredits?.toLocaleString(language),
            regularDailyCredits,
          })}
          open={showPopupBonusCredits}
          onClose={() => setShowPopupBonusCredits(false)}
          actionComponent={renderButtonGotIt(() =>
            setShowPopupBonusCredits(false),
          )}
        />

        <NotificationDialogV2
          showIcon={false}
          title={t('detailInfo')}
          description={t('lockCycleCreditsTooltipMyPackage')}
          open={showInfoCustomCredits}
          onClose={() => setShowInfoCustomCredits(false)}
          actionComponent={renderButtonGotIt(() =>
            setShowInfoCustomCredits(false),
          )}
        />
      </Box>
    </StyledRemainingCredits>
  );
};

export default RemainingCredits;
